import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Mail, RotateCcw, Anchor, Calendar, Clock, Users, Gift, Sparkles, Ship, MapPin, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { 
  BOAT_TYPES, 
  ADDONS, 
  TIME_SLOTS,
  LOCATIONS,
  TEXTS,
  TOUR_DURATION_HOURS,
  generateEmailContent 
} from '../constants';
import { ParentsCelebrating } from './svg/Characters';

const FinalReveal = ({ selections, onRestart }) => {
  const [emailSent, setEmailSent] = useState(false);
  
  const boat = BOAT_TYPES[selections.boatType];
  const addon = ADDONS.find(a => a.id === selections.addon);
  const timeSlot = TIME_SLOTS.find(t => t.value === selections.time);
  const location = LOCATIONS.find(l => l.id === selections.location);

  // Trigger celebration confetti
  useEffect(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = ['#FACC15', '#0EA5E9', '#EC4899', '#22C55E', '#F472B6'];
    
    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 80,
        origin: { x: 0, y: 0.8 },
        colors
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 80,
        origin: { x: 1, y: 0.8 },
        colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    
    // Initial big burst
    confetti({
      particleCount: 100,
      spread: 100,
      origin: { x: 0.5, y: 0.6 },
      colors
    });
    
    setTimeout(frame, 500);
  }, []);

  const mailtoLink = generateEmailContent(selections);
  
  const handleEmailClick = () => {
    setEmailSent(true);
    // Trigger extra celebration when email is sent
    confetti({
      particleCount: 150,
      spread: 120,
      origin: { x: 0.5, y: 0.5 },
      colors: ['#22C55E', '#FACC15', '#0EA5E9']
    });
  };

  return (
    <div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      data-testid="final-reveal-container"
    >
      {/* Background waves - BEHIND everything with lower z-index */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {/* Static wave layers to prevent animation issues */}
        <div className="absolute bottom-0 left-0 right-0 h-[30vh]">
          <svg 
            viewBox="0 0 1440 320" 
            className="absolute bottom-0 w-[200%] h-full"
            preserveAspectRatio="none"
            style={{ animation: 'wave-slide 20s linear infinite' }}
          >
            <path
              fill="rgba(56, 189, 248, 0.3)"
              d="M0,192L60,186.7C120,181,240,171,360,181.3C480,192,600,224,720,218.7C840,213,960,171,1080,165.3C1200,160,1320,192,1380,208L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
            />
          </svg>
          <svg 
            viewBox="0 0 1440 320" 
            className="absolute bottom-0 w-[200%] h-full"
            preserveAspectRatio="none"
            style={{ animation: 'wave-slide 15s linear infinite reverse' }}
          >
            <path
              fill="rgba(14, 165, 233, 0.4)"
              d="M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,138.7C672,128,768,160,864,181.3C960,203,1056,213,1152,197.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </svg>
          <svg 
            viewBox="0 0 1440 320" 
            className="absolute bottom-0 w-[200%] h-full"
            preserveAspectRatio="none"
            style={{ animation: 'wave-slide 25s linear infinite' }}
          >
            <path
              fill="rgba(2, 132, 199, 0.5)"
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,218.7C672,235,768,245,864,234.7C960,224,1056,192,1152,181.3C1248,171,1344,181,1392,186.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            />
          </svg>
        </div>
      </div>

      {/* Main content - ABOVE waves */}
      <div className="relative p-6 pb-8 flex flex-col flex-1" style={{ zIndex: 10 }}>
        {/* Celebration header */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
          className="text-center mb-4"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 0.5, repeat: 2 }}
            className="text-5xl mb-3"
          >
            🎉
          </motion.div>
          <h1 
            className="text-3xl font-black text-slate-900 mb-2"
            style={{ fontFamily: 'Nunito' }}
            data-testid="success-title"
          >
            {TEXTS.summaryTitle}
          </h1>
          <p className="text-lg text-slate-600">{TEXTS.summarySubtitle}</p>
        </motion.div>

        {/* Parents celebrating */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center mb-4"
        >
          <ParentsCelebrating />
        </motion.div>

        {/* Summary card */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="summary-card mb-4 bg-white shadow-xl"
          data-testid="summary-card"
        >
          <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-sky-100">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
              <Ship className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900">{boat?.name}</h3>
              <p className="text-sm text-sky-600">{boat?.tagline}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Ort
              </span>
              <span className="summary-value" data-testid="summary-location">
                {location?.name || '-'}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Datum
              </span>
              <span className="summary-value" data-testid="summary-date">
                {selections.date ? format(new Date(selections.date), 'PPP', { locale: de }) : '-'}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <Clock className="w-4 h-4" /> Zeit
              </span>
              <span className="summary-value" data-testid="summary-time">
                {timeSlot?.label || selections.time}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <Users className="w-4 h-4" /> Personen
              </span>
              <span className="summary-value" data-testid="summary-people">
                {selections.people}
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <Anchor className="w-4 h-4" /> {TEXTS.durationLabel}
              </span>
              <span className="summary-value">
                {TOUR_DURATION_HOURS} Stunden
              </span>
            </div>

            <div className="summary-row">
              <span className="summary-label flex items-center gap-2">
                <Gift className="w-4 h-4" /> Extra
              </span>
              <span className="summary-value" data-testid="summary-addon">
                {addon?.emoji} {addon?.name}
              </span>
            </div>

            {selections.customWish && (
              <div className="summary-row flex-col items-start gap-1">
                <span className="summary-label flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Besonderer Wunsch
                </span>
                <span className="summary-value text-sm" data-testid="summary-wish">
                  {selections.customWish}
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Important notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className={`p-4 rounded-xl mb-4 ${emailSent ? 'bg-green-100 border-2 border-green-400' : 'bg-yellow-100 border-2 border-yellow-400'}`}
        >
          {emailSent ? (
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-bold text-green-800">Mail geöffnet!</p>
                <p className="text-sm text-green-700">Jetzt nur noch abschicke, dann isch fertig!</p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="font-bold text-yellow-800 mb-1">⚠️ Wichtig!</p>
              <p className="text-sm text-yellow-700">
                Klick uff "Per Mail abschicke" damit mir wisse, dass du's ausgfüllt hosch!
              </p>
            </div>
          )}
        </motion.div>

        {/* Action buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1 }}
          className="space-y-3 mt-auto"
        >
          <motion.a
            href={mailtoLink}
            onClick={handleEmailClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`btn-sun w-full flex items-center justify-center gap-3 no-underline ${
              emailSent ? 'bg-gradient-to-r from-green-400 to-green-500' : ''
            }`}
            data-testid="send-email-button"
          >
            {emailSent ? <CheckCircle className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
            {emailSent ? 'Nochmal öffne' : TEXTS.sendButton}
          </motion.a>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onRestart}
            className="w-full py-4 px-6 rounded-full border-2 border-slate-200 text-slate-600 font-semibold flex items-center justify-center gap-2 bg-white"
            data-testid="restart-button"
          >
            <RotateCcw className="w-5 h-5" />
            {TEXTS.restartButton}
          </motion.button>
        </motion.div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="text-center text-xs text-slate-400 mt-4"
        >
          Hinweis: Dies ist ein Gutschein-Wunsch, keine verbindliche Buchung.
        </motion.p>
      </div>

      {/* CSS for wave animation */}
      <style>{`
        @keyframes wave-slide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default FinalReveal;
